/*
  Motureo: selektor pojazdu (marka → model), banery i karuzele kafli na stronie głównej oraz okno
  „Избери автомобил” z nagłówka.
  Dane aut: assets/motureo-auta.json (tools/motyw_auta.py z katalogu BG). Bez tego skryptu strona pokazuje
  formularz wyszukiwania po numerze OE (noscript), więc nic nie jest ukryte przed klientem ani robotem.
*/
(() => {
  'use strict';

  let dataPromise = null;

  const loadData = (url) => {
    if (!dataPromise) {
      dataPromise = fetch(url, { credentials: 'omit' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .catch((error) => {
          dataPromise = null;
          throw error;
        });
    }
    return dataPromise;
  };

  const option = (value, label) => {
    const el = document.createElement('option');
    el.value = value;
    el.textContent = label;
    return el;
  };

  const setStep = (step, state) => {
    if (!step) return;
    step.classList.toggle('motureo-step--active', state === 'active');
    step.classList.toggle('motureo-step--disabled', state === 'disabled');
  };

  const initFinder = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const makeSelect = root.querySelector('[data-finder-make]');
    const modelSelect = root.querySelector('[data-finder-model]');
    const submit = root.querySelector('[data-finder-submit]');
    const status = root.querySelector('[data-finder-status]');
    const stepMake = root.querySelector('[data-step="make"]');
    const stepModel = root.querySelector('[data-step="model"]');
    if (!makeSelect || !modelSelect || !submit) return;
    root.querySelector('[data-finder-app]')?.removeAttribute('hidden');
    const searchUrl = root.dataset.searchUrl || '/search';
    let makes = [];
    // pusta wartość listy (podpowiedź „wybierz…”) nie może stać się indeksem 0
    const picked = (select, list) => (select.value === '' ? undefined : list[Number(select.value)]);

    const targetFor = (model) => {
      if (model.url) return model.url;
      const params = new URLSearchParams({ q: model.q, type: 'product' });
      params.set('options[prefix]', 'last');
      params.set('options[unavailable_products]', 'last');
      return `${searchUrl}?${params.toString()}`;
    };

    const fillModels = () => {
      const make = picked(makeSelect, makes);
      modelSelect.replaceChildren(modelSelect.options[0]);
      modelSelect.value = '';
      submit.disabled = true;
      if (!make) {
        modelSelect.disabled = true;
        setStep(stepMake, 'active');
        setStep(stepModel, 'disabled');
        return;
      }
      make.models.forEach((model, index) => modelSelect.append(option(String(index), model.name)));
      modelSelect.disabled = false;
      setStep(stepMake, 'done');
      setStep(stepModel, 'active');
      modelSelect.focus();
    };

    const go = () => {
      const make = picked(makeSelect, makes);
      const model = make && picked(modelSelect, make.models);
      if (!model) return;
      window.location.assign(targetFor(model));
    };

    makeSelect.addEventListener('change', fillModels);
    modelSelect.addEventListener('change', () => {
      submit.disabled = modelSelect.value === '';
      if (modelSelect.value !== '') setStep(stepModel, 'done');
    });
    submit.addEventListener('click', go);
    modelSelect.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && modelSelect.value !== '') go();
    });

    // stan ładowania: lista marek nieaktywna, krótki komunikat, aria-busy (bez migania przy szybkim wczytaniu)
    makeSelect.disabled = true;
    root.setAttribute('aria-busy', 'true');
    const loadingTimer = window.setTimeout(() => {
      if (status && root.getAttribute('aria-busy') === 'true') status.textContent = root.dataset.loadingText || '';
    }, 300);

    loadData(root.dataset.dataUrl)
      .then((data) => {
        makes = Array.isArray(data.makes) ? data.makes : [];
        makes.forEach((make, index) => makeSelect.append(option(String(index), make.name)));
        makeSelect.disabled = false;
        window.clearTimeout(loadingTimer);
        root.removeAttribute('aria-busy');
        if (status) status.textContent = '';
      })
      .catch(() => {
        window.clearTimeout(loadingTimer);
        root.removeAttribute('aria-busy');
        makeSelect.disabled = true;
        if (status) status.textContent = root.dataset.errorText || '';
      });
  };

  const initBanners = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const track = root.querySelector('[data-banners-track]');
    const slides = track ? Array.from(track.children) : [];
    const dots = Array.from(root.querySelectorAll('[data-banners-dot]'));
    const prev = root.querySelector('[data-banners-prev]');
    const next = root.querySelector('[data-banners-next]');
    if (!track || slides.length < 2) {
      root.querySelector('[data-banners-nav]')?.setAttribute('hidden', '');
      return;
    }
    const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    const scrollTo = (index) => {
      const i = (index + slides.length) % slides.length;
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    };
    const update = () => {
      const i = current();
      dots.forEach((dot, index) => dot.setAttribute('aria-current', index === i ? 'true' : 'false'));
    };
    prev?.addEventListener('click', () => scrollTo(current() - 1));
    next?.addEventListener('click', () => scrollTo(current() + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => scrollTo(index)));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    update();
  };

  // Karuzela kafli: strzałki przewijają taśmę o 80 % szerokości; na końcach strzałka znika
  const initScroller = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const track = root.querySelector('[data-scroller-track]');
    if (!track) return;
    const prev = root.querySelector('[data-scroller-prev]');
    const next = root.querySelector('[data-scroller-next]');
    const step = () => Math.max(track.clientWidth * 0.8, 200);
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      root.classList.toggle('motureo-scroller--static', max <= 0);
      root.classList.toggle('motureo-scroller--start', track.scrollLeft <= 0);
      root.classList.toggle('motureo-scroller--end', track.scrollLeft >= max);
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    // ResizeObserver znika razem z elementem (bez nasłuchu na window po wymianie sekcji w edytorze)
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  };

  const initDialogs = () => {
    document.querySelectorAll('[data-finder-open]').forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', (event) => {
        const dialog = document.getElementById(button.getAttribute('aria-controls'));
        if (!dialog) return;
        event.preventDefault();
        dialog.querySelectorAll('[data-motureo-finder]').forEach(initFinder);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });
    document.querySelectorAll('[data-finder-close]').forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });
  };

  const init = () => {
    document.querySelectorAll('[data-motureo-finder]:not(dialog [data-motureo-finder])').forEach(initFinder);
    document.querySelectorAll('[data-motureo-banners]').forEach(initBanners);
    document.querySelectorAll('[data-motureo-scroller]').forEach(initScroller);
    initDialogs();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
